import { defineMessage, defineMessages } from 'react-intl'

import { CLOSE_SESSION_TYPES, LANDING_PAGE_INFO } from './types'

export const CALENDAR_LANDING_TITLES = defineMessages({
  [LANDING_PAGE_INFO.REFERRALS]: {
    defaultMessage: 'More referrals',
    description: 'Title for providers receiving more referrals ',
  },
  [LANDING_PAGE_INFO.FASTER_BOOKING]: {
    defaultMessage: 'Faster confirmed appointments',
    description: 'Title for providers getting appointments booked quicker',
  },
  [LANDING_PAGE_INFO.EASIER_MANAGEMENT]: {
    defaultMessage: 'Easier calendar management',
    description: 'Title for provider calendar management with ease',
  },
})

export const CALENDAR_LANDING_INFO = defineMessages({
  [LANDING_PAGE_INFO.REFERRALS]: {
    defaultMessage: 'Providers receive twice as many referrals when clients can book directly.',
    description: 'Explanation on better referrals for providers with calendars connected',
  },
  [LANDING_PAGE_INFO.FASTER_BOOKING]: {
    defaultMessage: 'Lyra members can book a session with you that fits both your schedule and theirs.',
    description: 'Explanation on ease of members booking with providers with calendars connected',
  },
  [LANDING_PAGE_INFO.EASIER_MANAGEMENT]: {
    defaultMessage:
      'We’ll only show Lyra members available spots you set up. Easily set recurring and one-time availability at the top of the hour or starting at the half-hour.',
    description: 'Explanation on calendar functionality and ease of use',
  },
})

export const UNSAVED_CHANGES_TITLE = defineMessages({
  HYBRID_SETUP: {
    defaultMessage: 'Do you want to exit Lyra calendar setup?',
    description: 'Title of modal for provider exiting calendar setup',
  },
  DEFAULT: {
    defaultMessage: 'You have unsaved changes',
    description: 'Title of modal where provider has unsaved changes',
  },
})

export const UNSAVED_CHANGES_CANCEL_TEXT = defineMessages({
  HYBRID_SETUP: {
    defaultMessage: 'Exit',
    description: 'Discard changes and exit calendar setup',
  },
  DEFAULT: {
    defaultMessage: 'Discard changes',
    description: 'Discard changes and navigate back',
  },
})

export const UNSAVED_CHANGES_CONFIRMATION_TEXT = defineMessages({
  HYBRID_SETUP: {
    defaultMessage: 'Continue setup',
    description: 'Stay on current page and continue setup',
  },
  DEFAULT: {
    defaultMessage: 'Continue editing',
    description: 'Stay on current page and continue editing template',
  },
})

export const UNSAVED_CHANGES_BODY_TEXT = defineMessages({
  HYBRID_SETUP: {
    defaultMessage: 'Your Lyra calendar won’t be live but your Google account will remain connected.',
    description: 'Body of modal where provider has unsaved changes and attempts to exit',
  },
  BOOKABLE_SETUP: {
    defaultMessage:
      'Changes will not be saved and your Lyra calendar will not be set up. You can return later and restart the setup.',
    description: 'Body of modal where provider has unsaved changes',
  },
  HYBRID_EDIT: {
    defaultMessage: 'Any changes you’ve made to your recurring availability will not be saved.',
    description: 'Body of modal where provider has unsaved changes for their availability',
  },
  BOOKABLE_EDIT: {
    defaultMessage: 'Any changes you’ve made to your calendar spots template will not be saved.',
    description: 'Body of modal where provider has unsaved changes',
  },
})

export const SAVE_CONFIRMATION_TITLE = defineMessages({
  HYBRID_SETUP: {
    defaultMessage: 'Ready to go live?',
    description: 'Title of modal to confirm provider calendar going live',
  },
  HYBRID_EDIT: {
    defaultMessage: 'Save recurring availability?',
    description: 'Title of modal to confirm update to recurring availability',
  },
  BOOKABLE: {
    defaultMessage: 'Save your calendar spots template?',
    description: 'Title of modal where provider is attempting to save availability',
  },
})

export const SAVE_CONFIRMATION_BUTTON_TEXT = defineMessages({
  HYBRID_SETUP: {
    defaultMessage: 'Continue',
    description: 'Button to save availability and go live',
  },
  DEFAULT: {
    defaultMessage: 'Save',
    description: 'Button to save availability',
  },
})

export const SAVE_CONFIRMATION_BODY_TEXT = defineMessages({
  HYBRID_SETUP: {
    defaultMessage:
      'We’ll remove any availability that conflicts with events from your external calendars. Once your calendar is live, clients will be able to see and book your open availability. You’ll be able to make changes on the next page once your calendar loads.',
    description:
      'Modal body text explaining how availability and external calendar choices effect the Lyra calendar experience once live',
  },
  BOOKABLE_SETUP: {
    defaultMessage:
      'Once saved, clients will be able to book during these times. You will also be able to adjust individual weekly availability as needed.',
    description: 'Body of modal where provider is attempting to save availability',
  },
  EDIT: {
    defaultMessage: 'Once saved, clients will be able to book during these times, starting with the current week.',
    description: 'Body of modal where provider is attempting to save availability',
  },
})

export const CALENDAR_SETUP_HEADER_TEXT = defineMessages({
  HYBRID_SETUP: {
    defaultMessage: 'Select recurring times you are available to see new clients',
    description: 'Header for calendar recurring availability setup',
  },
  BOOKABLE_SETUP: {
    defaultMessage: 'Set up your calendar spots template',
    description: 'Header for calendar setup',
  },
  HYBRID_EDIT: {
    defaultMessage: 'Edit recurring availability',
    description: 'Header for editing availability',
  },
  BOOKABLE_EDIT: {
    defaultMessage: 'Edit your recurring availability and working hours',
    description: 'Header for template edit',
  },
})

export const CALENDAR_SETUP_DESCRIPTION_TEXT = defineMessages({
  HYBRID: {
    defaultMessage:
      'You can adjust your availability later for specific weeks. We won’t surface availability to clients that conflict with your events from Google calendars. Available times can be set at the start or halfway through each hour.',
    description: 'Title for calendar recurring avialability setup',
  },
  BOOKABLE: {
    defaultMessage:
      'Clients can see and book these default times. You can adjust availability for specific weeks as needed.',
    description: 'Title for calendar setup',
  },
})

export const SELECT_CALENDARS_TITLE_TEXT = defineMessages({
  DEFAULT: {
    defaultMessage: 'Which calendars would you like to connect from your Google account?',
    description: 'Title text for external calendars',
  },
  TRANSITION: {
    defaultMessage: 'Confirm these are the calendars you want Lyra to continue to sync',
    description: 'Title text for confirming external calendars to sync',
  },
})

export const APPOINTMENTS_CALENDAR_SUBHEADER_TEXT = defineMessages({
  SETUP: {
    defaultMessage: 'Select the calendar you want appointments to show on',
    description: 'Subheader text for a section where an appointment calendar is selected',
  },
  EDIT: {
    defaultMessage: 'Calendar for appointments',
    description: 'Subheader text for external appointment calendar',
  },
})

export const READ_CALENDARS_SUBHEADER_TEXT = defineMessages({
  SETUP: {
    defaultMessage: 'Add your other calendars',
    description: 'Subheader text for a section where read calendars are selected',
  },
  EDIT: {
    defaultMessage: 'Other calendars',
    description: 'Subheader text for a section where read calendars can be changed',
  },
})

export const READ_CALENDARS_BODY_TEXT = defineMessages({
  SETUP: {
    defaultMessage:
      'Select any calendars you use to manage appointments. We’ll automatically block those times in your Lyra Calendar to prevent double-booking.',
    description: 'Body text explaining the functionality of additional read calendars',
  },
  EDIT: {
    defaultMessage: 'Events from these calendars block availability on the Lyra calendar.',
    description: 'Body text explaining the functionality of additional read calendars',
  },
})

const CLOSE_SESSION_TEXT = defineMessages({
  [CLOSE_SESSION_TYPES.NOTE_NEEDED]: {
    defaultMessage: 'Awaiting note',
    description: 'Text to let provider know they need to complete the note',
  },
  [CLOSE_SESSION_TYPES.INVOICE_NEEDED]: {
    defaultMessage: 'Awaiting invoice',
    description: 'Text to let provider know they need to complete the invoice',
  },
  [CLOSE_SESSION_TYPES.INVOICE_AND_NOTE_NEEDED]: {
    defaultMessage: 'Awaiting invoice and note',
    description: 'Text to let provider know they need to complete the note and invoice',
  },
  [CLOSE_SESSION_TYPES.INVOICE_NEEDED_BY_ADMIN]: {
    defaultMessage: 'Awaiting invoice by admin',
    description: 'Text to let provider know that the admin needs to complete the invoice',
  },
  [CLOSE_SESSION_TYPES.NOTE_NEEDED_BY_PROVIDER]: {
    defaultMessage: 'Awaiting note by provider',
    description: 'Text to let admin know that the provider needs to complete the note',
  },
})

export const CLOSE_SESSION_TOOLTIP_TEXT = {
  ...CLOSE_SESSION_TEXT,
  ...defineMessages({
    [CLOSE_SESSION_TYPES.SESSION_TO_CLOSE]: {
      defaultMessage: 'Awaiting close',
      description: 'Text to let provider know they need to complete the note',
    },
  }),
}

export const SESSION_INFO_TEXT = {
  ...CLOSE_SESSION_TEXT,
  ...defineMessages({
    [CLOSE_SESSION_TYPES.NOTE_AND_INVOICE_COMPLETED]: {
      defaultMessage: 'Client attended',
      description: 'Text to let provider know the client attended the session and the note and invoice are complete',
    },
    [CLOSE_SESSION_TYPES.LATE_CANCEL_COVERED]: {
      defaultMessage: 'Late cancel · Covered by employer',
      description: 'Text to let provider know this is a late cancellation covered by the employer',
    },
    [CLOSE_SESSION_TYPES.LATE_CANCEL_CLIENT_OWED]: {
      defaultMessage: 'Late cancel · Not covered by employer',
      description: 'Text to let provider know this is a late cancellation not covered by the employer',
    },
  }),
}

export const CLOSE_SESSION_BUTTON_TEXT = defineMessages({
  [CLOSE_SESSION_TYPES.NOTE_NEEDED]: {
    defaultMessage: 'Complete note',
    description: 'Button text to complete note for this session',
  },
  [CLOSE_SESSION_TYPES.INVOICE_NEEDED]: {
    defaultMessage: 'Complete invoice',
    description: 'Button text to complete invoice for this session',
  },
  [CLOSE_SESSION_TYPES.INVOICE_AND_NOTE_NEEDED]: {
    defaultMessage: 'Complete invoice and note',
    description: 'Button text to complete note and invoice for this session',
  },
  [CLOSE_SESSION_TYPES.NOTE_AND_INVOICE_COMPLETED]: {
    defaultMessage: 'View session note',
    description: 'Button text to view completed session note for this session',
  },
  [CLOSE_SESSION_TYPES.SESSION_TO_CLOSE]: {
    defaultMessage: 'Close session',
    description: 'Button text to close this session',
  },
})

export const CLOSE_SESSION_DURING_VIDEO_CALL_TOOLTIP = defineMessage({
  defaultMessage: 'Please hang up the video call before closing this session',
  description: 'Tooltip shown when closing a session is disabled because its video call is still in progress',
})
